/**
 * Which module (pre-invest, post-invest, relaties) the user was in, and where in
 * it. The shell's section switcher uses this to bring you back to the page you
 * left instead of the module start. Session storage only: a per-tab convenience.
 */

export type ModuleKey = 'preinvest' | 'postinvest' | 'relaties'

export const MODULE_START: Record<ModuleKey, string> = {
  preinvest: '/preinvest/board',
  postinvest: '/postinvest',
  relaties: '/relaties',
}

const PATH_KEY = (module: ModuleKey) => `vonk-last-path:${module}`
const LAST_KEY = 'vonk-last-module'

/** The module a pathname belongs to; `null` for /taken, /beveiliging and the rest. */
export function moduleOf(pathname: string): ModuleKey | null {
  for (const module of Object.keys(MODULE_START) as ModuleKey[]) {
    const prefix = `/${module}`
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return module
  }
  return null
}

/** Remember `pathname + search` for its module. Anything outside a module is ignored. */
export function rememberPath(location: string): void {
  const module = moduleOf(location.split(/[?#]/)[0])
  if (!module) return
  try {
    sessionStorage.setItem(PATH_KEY(module), location)
    sessionStorage.setItem(LAST_KEY, module)
  } catch {
    // Storage blocked: the switcher falls back to the module start.
  }
}

/** The last page seen in `module`, or the module start. */
export function lastPathFor(module: ModuleKey): string {
  try {
    const stored = sessionStorage.getItem(PATH_KEY(module))
    if (stored && moduleOf(stored.split(/[?#]/)[0]) === module) return stored
  } catch {
    // fall through
  }
  return MODULE_START[module]
}

/** The module visited most recently, or `null`. */
export function lastModule(): ModuleKey | null {
  try {
    const stored = sessionStorage.getItem(LAST_KEY)
    if (stored && stored in MODULE_START) return stored as ModuleKey
  } catch {
    // fall through
  }
  return null
}
